import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Plus, Search, ChevronDown, X, CheckCircle, XCircle, RotateCcw, Star, Pencil, Trash2, Share2, ExternalLink, FileText, BookOpen, Eye, Repeat2 } from "lucide-react";

/* ── Types ────────────────────────────────────────────────────────── */

interface RunResult {
  id: string;
  model: string;
  config: string;
  cheatsheet: string;
  problem: string;
  problemLabel: string;
  equation: string;
  answer: string;
  correct: boolean;
  error: boolean;
  time: number;
  cost: number;
  tokensIn: number;
  tokensOut: number;
  verdict: string;
  response: string;
  prompt: string;
  output: string;
  expected: string;
}

interface RunHistory {
  id: string;
  name: string;
  timestamp: string;
  models: string[];
  template: string;
  cheatsheet: string;
  problemCount: number;
  status: "DONE" | "FAILED";
  progress: string;
  accuracy: number;
  totalCost: number;
  totalTime: string;
  results: RunResult[];
  summary: SummaryRow[];
}

interface SummaryRow {
  model: string;
  template: string;
  cheatsheet: string;
  count: number;
  accuracy: number;
  avgCost: number;
  avgTime: string;
}

interface CustomProblem {
  id: string;
  eq1: string;
  eq2: string;
  goldAnswer: string;
}

/* ── Mock Data ────────────────────────────────────────────────────── */

const MODELS = [
  { key: "openai/gpt-5-mini" },
  { key: "deepseek/deepseek-v3.2" },
  { key: "google/gemini-3-flash-preview" },
  { key: "xai/grok-4.1-fast" },
];

const PROBLEMS_NORMAL = Array.from({ length: 9 }, (_, i) => ({
  id: `normal#${i + 1}`,
  label: `#${i + 1}: x = ${[
    "x * (y * ((z * x) * x)) → x = (y * ((z * z) * ...",
    "x * y * (z * ((w * u) * u)) → x = ((y * z) * x) * (x * w",
    "x = y * ((x * z) * (w * u)) → x = x * ((y * x) * x) * x...",
    "x * y = ((z * w) * u) * y → x = x * ((y * x) * (y * z))",
    "x * y = (y * z) * (z * (w * z)) → x * y = z * (w * (y...",
    "x = ((x * y) * (y * y)) * y → x * (y * x) = (y * x) = x * (y)...",
    "x = (y * (y * (y * z))) * x → x * y = z * (w * (w...",
    "x = x * (y * ((y * z) * y)) → x = x * ((x * (x * x...",
    "x = y * (z * ((w * u) * u)) → x = ((y * z) * x) * (x * w",
  ][i]}`,
  equation: [
    "x = x * (y * ((z * x) * x)) → x = (y * ((z * z) * y)) * w",
    "x * y * (z * ((w * u) * u)) → x = ((y * z) * x) * (x * w)",
    "x = y * ((x * z) * (w * u)) → x = x * ((y * x) * x) * x",
    "x * y = ((z * w) * u) * y → x = x * ((y * x) * (y * z))",
    "(x * y) * z = (z * y) * x → x * (y * x) = (x * z) * y",
    "x = ((x * y) * (y * y)) * y → x * (y * x) = (y * x) = x * (y)",
    "x = (y * (y * (y * z))) * x → x * y = z * (w * (w * z))",
    "(x * x) * x = (x * y) * x → x = (y * (x * (z * z))) * y",
    "x = y * (z * ((w * u) * u)) → x = ((y * z) * x) * (x * w)",
  ][i],
  gold: (["TRUE", "FALSE", "TRUE", "FALSE", "FALSE", "TRUE", "FALSE", "TRUE", "FALSE"] as const)[i],
  checked: [false, false, true, false, false, true, false, true, true][i],
}));

const PROBLEMS_HARD = Array.from({ length: 8 }, (_, i) => ({
  id: `hard#${i}`,
  label: `#${i}: x = ${[
    "y * (y * ((x * y) * x)) → x * y = ((z * x) * ...",
    "x * (y * z) = (z * w) * w → x * (y * x) = (x * y)...",
    "x = ((y * y) * z) * x → x = x * (((y * x) * x) * x...",
    "(x * y) * x = (x * z) * w → x * y * x = x * z * z...",
    "x = (y * z) * (z * x)) * y → x * x * x = (y * y)...",
    "x * x = (x * y) * z → x * x = y * z * w...",
    "x = (y * (y * (y * z))) * x → x * y = z * (w * (w...",
    "x = x * (y * ((y * z) * y)) → x = x * ((x * (x * x...",
  ][i]}`,
  equation: [
    "y * (y * ((x * y) * x)) → x * y = ((z * x) * x) * y",
    "x * (y * z) = (z * w) * w → x * (y * x) = (x * y) * z",
    "x = ((y * y) * z) * x → x = x * (((y * x) * x) * x) * y",
    "(x * y) * x = (x * z) * w → x * y * x = x * z * z * w",
    "x = (y * z) * (z * x) * y → x * x * x = (y * y) * z",
    "x * x = (x * y) * z → x * x = y * z * w * u",
    "x = (y * (y * (y * z))) * x → x * y = z * (w * (w * x))",
    "x = x * (y * ((y * z) * y)) → x = x * ((x * (x * x)) * z)",
  ][i],
  gold: (["FALSE", "TRUE", "FALSE", "FALSE", "TRUE", "FALSE", "TRUE", "FALSE"] as const)[i],
  checked: false,
}));

function generateMockResults(models: string[], template: string, cheatsheetId: string, problemIds: string[]): RunResult[] {
  const results: RunResult[] = [];
  for (const model of models) {
    for (const pid of problemIds) {
      const rand = Math.random();
      const isError = rand > 0.9;
      const correct = !isError && rand > 0.25;
      const timeFactor = model.includes("deepseek") ? 3 : model.includes("gpt") ? 15 : model.includes("gemini") ? 8 : 20;
      const tokIn = isError ? 0 : Math.floor(100 + Math.random() * 200);
      const tokOut = isError ? 0 : Math.floor(1000 + Math.random() * 3000);
      const outputVal = correct ? "TRUE" : "FALSE";
      const expectedVal = "TRUE";
      results.push({
        id: `${model}-${pid}-${Math.random().toString(36).slice(2, 6)}`,
        model: model,
        config: template,
        cheatsheet: cheatsheetId,
        problem: pid,
        problemLabel: pid,
        equation: `x = (z * z) * (z * (w * z)) → x * y = z * (w * (y * z))`,
        answer: outputVal,
        correct,
        error: isError,
        time: isError ? 0 : +(timeFactor + Math.random() * timeFactor).toFixed(1),
        cost: isError ? 0 : +(Math.random() * 0.01).toFixed(4),
        tokensIn: tokIn,
        tokensOut: tokOut,
        output: isError ? "--" : outputVal,
        expected: expectedVal,
        verdict: isError ? "ERROR: API timeout" : `VERDICT found: ${outputVal}, expected ${expectedVal}, correct: ${correct}`,
        response: isError ? "Error: Connection timeout" : `{"answer": "${correct ? "true" : "false"}", "reasoning": "By applying the equational theory..."}`,
        prompt: `You are an expert in equational theories of magmas.\n\nEquation 1: {{ equation1 }}\nEquation 2: {{ equation2 }}\n\nTask:\nDetermine whether Equation 1 implies Equation 2 over all magmas.`,
      });
    }
  }
  return results;
}

function generateSummary(models: string[], template: string, cheatsheetId: string, results: RunResult[]): SummaryRow[] {
  return models.map((m) => {
    const modelResults = results.filter((r) => r.model === m);
    const correctCount = modelResults.filter((r) => r.correct).length;
    const avgCost = modelResults.reduce((s, r) => s + r.cost, 0) / (modelResults.length || 1);
    const avgTime = modelResults.reduce((s, r) => s + r.time, 0) / (modelResults.length || 1);
    return {
      model: m,
      template,
      cheatsheet: cheatsheetId === "none" ? "none" : cheatsheetId,
      count: modelResults.length,
      accuracy: modelResults.length ? (correctCount / modelResults.length) * 100 : 0,
      avgCost: +avgCost.toFixed(4),
      avgTime: avgTime.toFixed(1) + "s",
    };
  });
}

/* ── Source Dropdown Data ─────────────────────────────────────────── */

const SOURCE_MY_CHEATSHEETS = [
  { value: "scratch", label: "— Start from scratch —", isDefault: true },
  { value: "proof_first", label: "cheatsheet_proof_first" },
  { value: "fast_filters_micro", label: "cheatsheet_fast_filters_micro" },
  { value: "counterexample_first", label: "cheatsheet_counterexample_first" },
  { value: "balanced_hybrid", label: "cheatsheet_balanced_hybrid" },
];

const SOURCE_OFFICIAL_EXAMPLES = [
  { value: "official_cot_basic", label: "example_chain_of_thought_basic" },
  { value: "official_step_by_step", label: "example_step_by_step_proof" },
  { value: "official_induction", label: "example_induction_template" },
];

const SOURCE_MY_FAVORITES: { 
  value: string; 
  label: string; 
  author: string; 
  publishedAt: string;
  shareItemId?: string;
  isAvailable: boolean;
  authorAvatar?: string;
  referenceCount?: number;
  favoriteCount?: number;
}[] = [
  { value: "fav_cot_decomp", label: "Chain-of-Thought Decomposition", author: "Bob Lin", publishedAt: "2026-03-15 21:45 UTC", shareItemId: "2", isAvailable: true, authorAvatar: "https://images.unsplash.com/photo-1554765345-6ad6a5417cde?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtYW4lMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080", referenceCount: 18, favoriteCount: 9 },
  { value: "fav_proof_strat", label: "Proof Problem Strategies", author: "Alice Chen", publishedAt: "2026-03-12 14:30 UTC", shareItemId: "4", isAvailable: true, authorAvatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080", referenceCount: 15, favoriteCount: 7 },
  { value: "fav_error_analysis", label: "Error Analysis Framework", author: "James Park", publishedAt: "2026-03-10 09:15 UTC", isAvailable: false, authorAvatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYW4lMjBwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdHxlbnwxfHx8fDE3NzM2MzQzNTF8MA&ixlib=rb-4.1.0&q=80&w=1080", referenceCount: 22, favoriteCount: 14 }, // This item was deleted from Share Board
];

const FAVORITE_PREFILL: Record<string, { title: string; content: string }> = {
  fav_cot_decomp: {
    title: "Chain-of-Thought Decomposition",
    content: "Step 1: Identify the premise equations and target implication.\nStep 2: Decompose into sub-goals using transitivity.\nStep 3: Apply each lemma sequentially, verifying intermediate steps.\nStep 4: Conclude with a direct proof or construct a counterexample magma.",
  },
  fav_proof_strat: {
    title: "Proof Problem Strategies",
    content: "Strategy A — Direct substitution: substitute specific element patterns to test both directions.\nStrategy B — Canonical form: reduce both sides to a canonical magma representation.\nStrategy C — Finite model check: enumerate small magmas (|M| ≤ 4) for fast falsification.",
  },
  fav_error_analysis: {
    title: "Error Analysis Framework",
    content: "Common pitfalls:\n• Confusing implication direction (A⟹B vs B⟹A)\n• Overlooking non-associative edge cases in free magmas\n• Incorrect counterexample size (use smallest possible)\nVerification checklist: re-check substitution, confirm all variables are universally quantified.",
  },
};

function getSourceLabel(value: string): string {
  const all = [...SOURCE_MY_CHEATSHEETS, ...SOURCE_OFFICIAL_EXAMPLES, ...SOURCE_MY_FAVORITES];
  return all.find((o) => o.value === value)?.label ?? value;
}

/* ── Source Dropdown Component ────────────────────────────────────── */

type SourceTab = "mine" | "official" | "favorites";

const SOURCE_TABS: { id: SourceTab; label: string }[] = [
  { id: "mine", label: "My Cheatsheets" },
  { id: "official", label: "Official" },
  { id: "favorites", label: "My Favorites" },
];

function SourceDropdown({
  value,
  onChange,
  onBrowseShareBoard,
}: {
  value: string;
  onChange: (v: string) => void;
  onBrowseShareBoard?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<SourceTab>("mine");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (v: string) => {
    onChange(v);
    setOpen(false);
  };

  const BORDER = "1px solid rgba(59,14,39,0.15)";
  const ITEM_BASE = "w-full bg-transparent border-none cursor-pointer text-left px-[14px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] hover:bg-[rgba(59,14,39,0.04)] transition-colors block";

  const visibleItems =
    activeTab === "mine"
      ? SOURCE_MY_CHEATSHEETS
      : activeTab === "official"
      ? SOURCE_OFFICIAL_EXAMPLES
      : SOURCE_MY_FAVORITES;

  return (
    <div className="relative w-full" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-[40px] px-[14px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] cursor-pointer focus:outline-none flex items-center justify-between"
        style={{ border: BORDER }}
      >
        <span className="truncate text-left">{getSourceLabel(value)}</span>
        <ChevronDown
          size={14}
          className="shrink-0 ml-[8px] text-[rgba(59,14,39,0.3)] transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
        />
      </button>

      {open && (
        <div
          className="absolute top-[calc(100%+3px)] left-0 right-0 z-[200] bg-white"
          style={{ border: BORDER, boxShadow: "0 6px 20px rgba(59,14,39,0.12)" }}
        >
          {/* Tab bar */}
          <div className="flex" style={{ borderBottom: "1px solid rgba(59,14,39,0.1)" }}>
            {SOURCE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className="flex-1 bg-transparent border-none cursor-pointer px-[6px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[11px] tracking-[0.03em] transition-colors"
                style={{
                  color: activeTab === tab.id ? "#3b0e27" : "rgba(59,14,39,0.4)",
                  borderBottom: activeTab === tab.id ? "2px solid #3b0e27" : "2px solid transparent",
                  marginBottom: "-1px",
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Items list */}
          <div className="max-h-[240px] overflow-y-auto py-[4px]">
            {visibleItems.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => select(opt.value)}
                className={ITEM_BASE}
                style={{
                  color: value === opt.value ? "#3b0e27" : "rgba(59,14,39,0.7)",
                  backgroundColor: value === opt.value ? "rgba(59,14,39,0.05)" : undefined,
                  fontStyle: (opt as any).isDefault ? "italic" : undefined,
                }}
              >
                {opt.label}
              </button>
            ))}

            {/* Browse Share Board — only in Favorites tab */}
            {activeTab === "favorites" && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onBrowseShareBoard?.();
                }}
                className="w-full bg-transparent border-none cursor-pointer text-left px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] hover:bg-[rgba(59,14,39,0.04)] transition-colors flex items-center gap-[6px]"
                style={{
                  color: "rgba(59,14,39,0.45)",
                  borderTop: "1px solid rgba(59,14,39,0.1)",
                  marginTop: "4px",
                }}
              >
                <ExternalLink size={12} />
                Browse Share Board
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── New Cheatsheet Modal ────────────────────────────────────────── */

function NewCheatsheetModal({ onClose, onCreate, onBrowseShareBoard, onViewShareItem }: { onClose: () => void; onCreate: (title: string, content: string) => void; onBrowseShareBoard?: () => void; onViewShareItem?: (itemId: string) => void }) {
  const [source, setSource] = useState("scratch");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const isFavorite = SOURCE_MY_FAVORITES.some((f) => f.value === source);
  const favoriteRef = isFavorite ? SOURCE_MY_FAVORITES.find((f) => f.value === source) : null;

  const handleSourceChange = (v: string) => {
    setSource(v);
    const fav = SOURCE_MY_FAVORITES.find((f) => f.value === v);
    if (fav && FAVORITE_PREFILL[v]) {
      setTitle(FAVORITE_PREFILL[v].title);
      setContent(FAVORITE_PREFILL[v].content);
    } else if (v === "scratch") {
      setTitle("");
      setContent("");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-[640px] max-h-[90vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-[28px] pt-[28px] pb-[20px]">
          <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[20px] text-[#3b0e27] tracking-[-0.4px]">
            New Cheatsheet
          </p>
        </div>
        <div className="px-[28px] flex flex-col gap-[16px]">
          {/* Source dropdown */}
          <SourceDropdown
            value={source}
            onChange={handleSourceChange}
            onBrowseShareBoard={() => {
              onClose();
              onBrowseShareBoard?.();
            }}
          />
          {/* Referenced from Share Board */}
          {isFavorite && favoriteRef && (
            <div
              className="px-[16px] py-[14px]"
              style={{ backgroundColor: "rgba(59,14,39,0.03)", border: "1px solid rgba(59,14,39,0.1)" }}
            >
              <p
                className="font-['Helvetica_Neue:Medium',sans-serif] text-[13px] text-[#3b0e27] m-0 mb-[6px]"
              >
                Referenced from Share Board
              </p>
              {favoriteRef.isAvailable ? (
                <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0" style={{ color: "rgba(59,14,39,0.5)" }}>
                  {favoriteRef.author}
                  <span className="mx-[6px]">·</span>
                  Published at {favoriteRef.publishedAt}
                  <span className="mx-[6px]">·</span>
                  <button
                    type="button"
                    className="bg-transparent border-none cursor-pointer p-0 font-['Helvetica_Neue:Regular',sans-serif] text-[12px] underline underline-offset-2"
                    style={{ color: "rgba(59,14,39,0.5)" }}
                    onClick={() => { 
                      onClose(); 
                      if (favoriteRef.shareItemId && onViewShareItem) {
                        onViewShareItem(favoriteRef.shareItemId);
                      }
                    }}
                  >
                    View original Share Board item
                  </button>
                </p>
              ) : (
                <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0" style={{ color: "rgba(198,40,40,0.6)" }}>
                  {favoriteRef.author}
                  <span className="mx-[6px]">·</span>
                  Published at {favoriteRef.publishedAt}
                  <span className="mx-[6px]">·</span>
                  <span className="italic">Original item no longer available</span>
                </p>
              )}
            </div>
          )}
          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full h-[40px] px-[14px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.3)] focus:outline-none focus:border-[rgba(59,14,39,0.3)]"
            style={{ border: '1px solid rgba(59,14,39,0.15)' }}
          />
          {/* Content */}
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Cheatsheet content..."
            rows={6}
            className="w-full px-[14px] py-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.3)] focus:outline-none focus:border-[rgba(59,14,39,0.3)] resize-none leading-[22px]"
            style={{ border: '1px solid rgba(59,14,39,0.15)' }}
          />
          {/* Prompt Preview */}
          <div>
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[rgba(59,14,39,0.5)] mb-[8px]">
              Prompt preview:
            </p>
            <div className="bg-[rgba(59,14,39,0.02)] px-[16px] py-[14px]" style={{ border: '1px solid rgba(59,14,39,0.1)' }}>
              <pre className="font-['Menlo',monospace] text-[11px] text-[rgba(59,14,39,0.6)] leading-[18px] whitespace-pre-wrap m-0">
{`You are an expert in equational theories of magmas.

Equation 1: {{ equation1 }}
Equation 2: {{ equation2 }}

Task:
Determine whether Equation 1 implies Equation 2 over all magmas.

Output format (use exact headers):
VERDICT: TRUE or FALSE
REASONING:
PROOF:`}
              </pre>
            </div>
          </div>
        </div>
        <div className="px-[28px] py-[20px] flex items-center justify-end gap-[12px]">
          <button
            onClick={onClose}
            className="px-[20px] h-[36px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[rgba(59,14,39,0.6)] bg-transparent border border-[rgba(59,14,39,0.15)] cursor-pointer hover:bg-[rgba(59,14,39,0.03)]"
          >
            Cancel
          </button>
          <button
            onClick={() => { onCreate(title, content); onClose(); }}
            className="px-[20px] h-[36px] font-['Helvetica_Neue:Medium',sans-serif] text-[13px] text-white bg-[#3B0E27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Cheatsheet mock data for edit prefill ───────────────────────── */

const CHEATSHEET_DATA: Record<string, { title: string; content: string }> = {
  cheatsheet_proof_first: {
    title: "cheatsheet_proof_first",
    content: "Strategy: Always attempt a direct proof before constructing a counterexample.\n1. Identify the universal quantifiers in both equations.\n2. Try substitution of specific elements to reduce to a known identity.\n3. Use transitivity of equality to chain intermediate steps.\n4. If proof fails after 3 attempts, pivot to counterexample search.",
  },
  cheatsheet_fast_filters_micro: {
    title: "cheatsheet_fast_filters_micro",
    content: "Fast-filter heuristics for magma implication:\n• Check arity mismatch — if lhs/rhs depths differ by >2, likely FALSE.\n• Test on the two-element magma first (cheapest falsification).\n• Apply idempotency filter: if A is idempotent and B is not, FALSE.\n• Use commutativity shortcut when both sides are symmetric.",
  },
  cheatsheet_counterexample_first: {
    title: "cheatsheet_counterexample_first",
    content: "Counterexample-first approach:\n1. Enumerate magmas of size 2, then 3.\n2. For each magma M, evaluate LHS and RHS under all assignments.\n3. If LHS ≠ RHS for any assignment → output FALSE with the witness.\n4. If no counterexample found up to size 4 → attempt formal proof.",
  },
};

/* ── Edit Cheatsheet Modal ───────────────────────────────────────── */

function EditCheatsheetModal({
  cheatsheetKey,
  onClose,
  onSave,
}: {
  cheatsheetKey: string;
  onClose: () => void;
  onSave: (key: string, title: string, content: string) => void;
}) {
  const prefill = CHEATSHEET_DATA[cheatsheetKey] ?? { title: cheatsheetKey, content: "" };
  const [title, setTitle] = useState(prefill.title);
  const [content, setContent] = useState(prefill.content);
  const BORDER = "1px solid rgba(59,14,39,0.15)";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white w-[640px] max-h-[90vh] overflow-y-auto flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-[28px] pt-[28px] pb-[20px] flex items-center justify-between">
          <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[20px] text-[#3b0e27] tracking-[-0.4px] m-0">
            Edit Cheatsheet
          </p>
          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer p-[4px] text-[rgba(59,14,39,0.3)] hover:text-[rgba(59,14,39,0.6)] transition-colors"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-[28px] flex flex-col gap-[16px]">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="w-full h-[40px] px-[14px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.3)] focus:outline-none"
            style={{ border: BORDER }}
          />
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Cheatsheet content..."
            rows={8}
            className="w-full px-[14px] py-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.3)] focus:outline-none resize-none leading-[22px]"
            style={{ border: BORDER }}
          />
        </div>
        <div className="px-[28px] py-[20px] flex items-center justify-end gap-[12px]">
          <button
            onClick={onClose}
            className="px-[20px] h-[36px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[rgba(59,14,39,0.6)] bg-transparent border border-[rgba(59,14,39,0.15)] cursor-pointer hover:bg-[rgba(59,14,39,0.03)]"
          >
            Cancel
          </button>
          <button
            onClick={() => { onSave(cheatsheetKey, title, content); onClose(); }}
            className="px-[20px] h-[36px] font-['Helvetica_Neue:Medium',sans-serif] text-[13px] text-white bg-[#3B0E27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Left Config Panel ───────────────────────────────────────────── */

function ConfigPanel({
  onRun,
  onNavigateToShareBoard,
}: {
  onRun: (config: { models: string[]; template: string; problems: string[]; cheatsheet: string }) => void;
  onNavigateToShareBoard?: () => void;
}) {
  const [template, setTemplate] = useState<"full" | "basic">("basic");
  const [selectedModel, setSelectedModel] = useState(MODELS[0].key); // openai/gpt-5-mini
  const [cheatsheet, setCheatsheet] = useState("none");
  const [problemSet, setProblemSet] = useState<"normal" | "hard" | "custom">("normal");
  const [selectedNormal, setSelectedNormal] = useState<Set<string>>(new Set(["normal#3", "normal#6", "normal#8", "normal#9"]));
  const [selectedHard, setSelectedHard] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [showCheatsheetModal, setShowCheatsheetModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showShareConfirmModal, setShowShareConfirmModal] = useState(false);
  const [customProblems, setCustomProblems] = useState<CustomProblem[]>([]);
  const [customEq1, setCustomEq1] = useState("");
  const [customEq2, setCustomEq2] = useState("");
  const [customGold, setCustomGold] = useState("");

  const selectedProblems = problemSet === "normal" ? selectedNormal : problemSet === "hard" ? selectedHard : new Set<string>();

  const toggleProblem = (id: string) => {
    if (problemSet === "normal") {
      setSelectedNormal((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    } else if (problemSet === "hard") {
      setSelectedHard((prev) => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
    }
  };

  const problems = problemSet === "normal" ? PROBLEMS_NORMAL : problemSet === "hard" ? PROBLEMS_HARD : [];
  const filteredProblems = searchQuery
    ? problems.filter((p) => p.label.toLowerCase().includes(searchQuery.toLowerCase()) || p.id.toLowerCase().includes(searchQuery.toLowerCase()))
    : problems;

  const handleRun = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmRun = () => {
    setShowConfirmModal(false);
    const pids = [...selectedProblems];
    if (pids.length === 0 && problemSet !== "custom") {
      const defaults = problems.slice(0, 3).map((p) => p.id);
      onRun({ models: [selectedModel], template, problems: defaults, cheatsheet });
    } else {
      onRun({ models: [selectedModel], template, problems: pids.length > 0 ? pids : ["custom#0"], cheatsheet });
    }
  };

  const confirmProblemCount = (() => {
    const pids = [...selectedProblems];
    if (pids.length === 0 && problemSet !== "custom") return Math.min(3, problems.length);
    if (problemSet === "custom") return customProblems.length;
    return pids.length;
  })();

  const addCustomProblem = () => {
    if (!customEq1) return;
    setCustomProblems((prev) => [
      ...prev,
      { id: `custom#${prev.length}`, eq1: customEq1, eq2: customEq2, goldAnswer: customGold },
    ]);
    setCustomEq1("");
    setCustomEq2("");
    setCustomGold("");
  };

  return (
    <div className="flex flex-col h-full">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden px-[24px] py-[24px] flex flex-col gap-[24px] min-w-0">
        {/* Template */}
        <div>
          <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[14px] text-[#3b0e27] mb-[10px]">
            Template
          </p>
          <div className="relative">
            <select
              value={template}
              onChange={(e) => setTemplate(e.target.value as "full" | "basic")}
              className="w-full h-[38px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] appearance-none cursor-pointer focus:outline-none focus:border-[rgba(59,14,39,0.3)]"
              style={{ border: '1px solid #3b0e2726' }}
            >
              <option value="basic">basic</option>
              <option value="full">full</option>
            </select>
            <ChevronDown size={14} className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
          </div>
        </div>

        {/* Cheatsheet */}
        <div>
          <div className="flex items-center justify-between mb-[10px]">
            <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[14px] text-[#3b0e27]">
              Cheatsheet
            </p>
            <div className="flex items-center gap-[8px]">
              <button
                onClick={onNavigateToShareBoard}
                className="px-[10px] py-[3px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] bg-transparent cursor-pointer hover:bg-[rgba(59,14,39,0.03)]"
                style={{ border: '1px solid #3b0e27' }}
                title="Open Share Board"
              >
                Open Share Board
              </button>
              <button
                onClick={() => setShowCheatsheetModal(true)}
                className="px-[10px] py-[3px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] bg-transparent cursor-pointer hover:bg-[rgba(59,14,39,0.03)]"
                style={{ border: '1px solid #3b0e27' }}
              >
                + New
              </button>
            </div>
          </div>
          <div className="relative">
            <select
              value={cheatsheet}
              onChange={(e) => setCheatsheet(e.target.value)}
              className="w-full h-[38px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] appearance-none cursor-pointer focus:outline-none focus:border-[rgba(59,14,39,0.3)]"
              style={{ border: '1px solid #3b0e2726' }}
            >
              <option value="none">None (no cheatsheet)</option>
              <option value="cheatsheet_proof_first">cheatsheet_proof_first</option>
              <option value="cheatsheet_fast_filters_micro">cheatsheet_fast_filters_micro</option>
              <option value="cheatsheet_counterexample_first">cheatsheet_counterexample_first</option>
            </select>
            <ChevronDown size={14} className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
          </div>

          {/* Action buttons — only shown when a cheatsheet is selected */}
          {cheatsheet !== "none" && (
            <div className="flex items-center gap-[2px] mt-[8px]" style={{ borderTop: "1px solid rgba(59,14,39,0.07)", paddingTop: "8px" }}>
              {/* Edit */}
              <button
                onClick={() => setShowEditModal(true)}
                title="Edit cheatsheet"
                className="flex items-center gap-[5px] px-[10px] py-[5px] bg-transparent border-none cursor-pointer hover:bg-[rgba(59,14,39,0.05)] transition-colors"
                style={{ color: "rgba(59,14,39,0.5)" }}
              >
                <Pencil size={13} />
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]">Edit</span>
              </button>

              {/* Delete */}
              <button
                onClick={() => setCheatsheet("none")}
                title="Delete cheatsheet"
                className="flex items-center gap-[5px] px-[10px] py-[5px] bg-transparent border-none cursor-pointer hover:bg-[rgba(59,14,39,0.05)] transition-colors"
                style={{ color: "rgba(59,14,39,0.5)" }}
              >
                <Trash2 size={13} />
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]">Delete</span>
              </button>

              {/* Share */}
              <button
                onClick={() => {
                  if (onNavigateToShareBoard) {
                    onNavigateToShareBoard();
                  }
                }}
                title="Share to Share Board"
                className="flex items-center gap-[5px] px-[10px] py-[5px] bg-transparent border-none cursor-pointer hover:bg-[rgba(59,14,39,0.05)] transition-colors"
                style={{ color: "rgba(59,14,39,0.5)" }}
              >
                <Share2 size={13} />
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px]">Publish to Share Board</span>
              </button>
            </div>
          )}
        </div>

        {/* Model */}
        <div>
          <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[14px] text-[#3b0e27] mb-[10px]">
            Model
          </p>
          <div className="relative">
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full h-[38px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[#3b0e27] appearance-none cursor-pointer focus:outline-none focus:border-[rgba(59,14,39,0.3)]"
              style={{ border: '1px solid #3b0e2726' }}
            >
              {MODELS.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.key}
                </option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-[12px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
          </div>
        </div>

        {/* Problems */}
        <div>
          <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[14px] text-[#3b0e27] mb-[10px]">
            Problems
            {(selectedProblems.size > 0 || (problemSet === "custom" && customProblems.length > 0)) && (
              <span className="text-[rgba(59,14,39,0.4)] ml-[4px]">
                {problemSet === "custom" ? customProblems.length : selectedProblems.size}
              </span>
            )}
          </p>
          {/* Search */}
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder='Quick search (e.g. "s5" → short#5)'
            className="w-full h-[34px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.35)] focus:outline-none focus:border-[rgba(59,14,39,0.3)] mb-[10px]"
            style={{ border: '1px solid #3b0e2726' }}
          />
          {/* Tabs */}
          <div className="flex items-center gap-0 mb-[10px]">
            {([
              { key: "normal" as const, label: "Normal", count: "1000" },
              { key: "hard" as const, label: "Hard", count: "200" },
              { key: "custom" as const, label: "Custom", count: null },
            ]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setProblemSet(tab.key)}
                className={`px-[14px] py-[8px] font-['Helvetica_Neue:Regular',sans-serif] text-[13px] cursor-pointer border-b-2 border-x-0 border-t-0 bg-transparent transition-colors flex items-center gap-[5px] ${
                  problemSet === tab.key
                    ? "text-[#3b0e27] border-b-[#3b0e27]"
                    : "text-[rgba(59,14,39,0.4)] border-b-transparent hover:text-[rgba(59,14,39,0.7)]"
                }`}
              >
                {tab.label}
                {tab.count && (
                  <span className={`font-['Helvetica_Neue:Regular',sans-serif] text-[11px] ${
                    problemSet === tab.key ? "text-[rgba(59,14,39,0.45)]" : "text-[rgba(59,14,39,0.3)]"
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Problem list (normal/hard) */}
          {problemSet !== "custom" && (
            <div className="flex flex-col gap-[2px] max-h-[300px] overflow-y-auto">
              {filteredProblems.map((p) => {
                const isChecked = selectedProblems.has(p.id);
                return (
                  <label
                    key={p.id}
                    className="flex items-start gap-[8px] py-[6px] cursor-pointer hover:bg-[rgba(59,14,39,0.02)] px-[4px]"
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleProblem(p.id)}
                      className="appearance-none size-[14px] cursor-pointer mt-[3px] shrink-0 rounded-none transition-colors"
                      style={{
                        border: '1px solid rgba(59,14,39,0.3)',
                        backgroundColor: isChecked ? '#3b0e27' : 'white',
                        backgroundImage: isChecked
                          ? `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C%2Fsvg%3E")`
                          : 'none',
                        backgroundSize: '100% 100%',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                      }}
                    />
                    <span className={`font-['Helvetica_Neue:Regular',sans-serif] text-[13px] leading-[20px] truncate min-w-0 ${
                      isChecked ? "text-[#3b0e27]" : "text-[rgba(59,14,39,0.6)]"
                    }`}>
                      {p.label}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {/* Custom problems */}
          {problemSet === "custom" && (
            <div className="flex flex-col gap-[10px] min-w-0 w-full overflow-hidden">
              <input
                type="text"
                value={customEq1}
                onChange={(e) => setCustomEq1(e.target.value)}
                placeholder="Equation 1 (e.g. x*(y*z) = ...)"
                className="w-full h-[34px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.35)] focus:outline-none focus:border-[rgba(59,14,39,0.3)] box-border"
                style={{ border: '1px solid #3b0e2726' }}
              />
              <input
                type="text"
                value={customEq2}
                onChange={(e) => setCustomEq2(e.target.value)}
                placeholder="Equation 2"
                className="w-full h-[34px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[#3b0e27] placeholder:text-[rgba(59,14,39,0.35)] focus:outline-none focus:border-[rgba(59,14,39,0.3)] box-border"
                style={{ border: '1px solid #3b0e2726' }}
              />
              <div className="flex gap-[8px] items-center w-full min-w-0">
                <div className="relative flex-1 min-w-0">
                  <select
                    value={customGold}
                    onChange={(e) => setCustomGold(e.target.value)}
                    className="w-full h-[34px] px-[12px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[#3b0e27] appearance-none cursor-pointer focus:outline-none box-border"
                    style={{ border: '1px solid #3b0e2726' }}
                  >
                    <option value="">Gold answer</option>
                    <option value="TRUE">TRUE</option>
                    <option value="FALSE">FALSE</option>
                  </select>
                  <ChevronDown size={12} className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
                </div>
                <button
                  onClick={addCustomProblem}
                  className="px-[16px] h-[34px] font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-white bg-[#3B0E27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors whitespace-nowrap shrink-0"
                >
                  Add Problem
                </button>
              </div>
              {customProblems.length > 0 && (
                <div className="flex flex-col gap-[4px] mt-[4px]">
                  {customProblems.map((cp) => (
                    <div key={cp.id} className="flex items-center gap-[8px] px-[8px] py-[6px] bg-[rgba(59,14,39,0.03)]" style={{ border: '1px solid #3b0e2714' }}>
                      <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.6)] flex-1 truncate">
                        {cp.eq1} → {cp.eq2}
                      </span>
                      {cp.goldAnswer && (
                        <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[10px] text-[rgba(59,14,39,0.4)]">
                          {cp.goldAnswer}
                        </span>
                      )}
                      <button
                        onClick={() => setCustomProblems((prev) => prev.filter((p) => p.id !== cp.id))}
                        className="bg-transparent border-none cursor-pointer p-0 text-[rgba(59,14,39,0.3)] hover:text-[rgba(59,14,39,0.6)]"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Problems + Run Button */}
      <div className="shrink-0 px-[24px] py-[16px]" style={{ borderTop: '1px solid rgba(59,14,39,0.08)' }}>
        {/* Selected problem cards */}
        {selectedProblems.size > 0 && problemSet !== "custom" && (
          <div className="mb-[12px]">
            <div className="flex items-center justify-between mb-[8px]">
              <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[11px] text-[rgba(59,14,39,0.45)] uppercase tracking-[0.5px]">
                Selected ({selectedProblems.size})
              </span>
              <button
                onClick={() => {
                  if (problemSet === "normal") setSelectedNormal(new Set());
                  else if (problemSet === "hard") setSelectedHard(new Set());
                }}
                className="bg-transparent border-none cursor-pointer p-0 font-['Helvetica_Neue:Regular',sans-serif] text-[11px] text-[rgba(59,14,39,0.35)] hover:text-[rgba(59,14,39,0.6)] transition-colors"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-col gap-[8px] max-h-[240px] overflow-y-auto">
              {[...selectedProblems].map((pid) => {
                const p = (problemSet === "normal" ? PROBLEMS_NORMAL : PROBLEMS_HARD).find((pr) => pr.id === pid);
                if (!p) return null;
                const isTrue = p.gold === "TRUE";
                return (
                  <div
                    key={pid}
                    className="bg-white px-[14px] py-[10px] relative"
                    style={{
                      border: '1px solid rgba(59,14,39,0.12)',
                      borderLeft: isTrue ? '3px solid rgba(45,138,78,0.5)' : '3px solid rgba(198,40,40,0.5)',
                    }}
                  >
                    <div className="flex items-center justify-between mb-[6px]">
                      <div className="flex items-center gap-[8px]">
                        <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[13px] text-[#3b0e27]">
                          {pid}
                        </span>
                        <span
                          className="px-[6px] py-[1px] font-['Helvetica_Neue:Medium',sans-serif] text-[10px]"
                          style={{
                            backgroundColor: isTrue ? 'rgba(45,138,78,0.1)' : 'rgba(198,40,40,0.1)',
                            color: isTrue ? '#2d8a4e' : '#c62828',
                          }}
                        >
                          {p.gold}
                        </span>
                      </div>
                      <button
                        onClick={() => toggleProblem(pid)}
                        className="bg-transparent border-none cursor-pointer p-0 text-[rgba(59,14,39,0.3)] hover:text-[rgba(59,14,39,0.6)] transition-colors"
                      >
                        <X size={14} />
                      </button>
                    </div>
                    <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.55)] leading-[18px] m-0 break-all">
                      {p.equation}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {problemSet === "custom" && customProblems.length > 0 && (
          <div className="mb-[12px]">
            <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[11px] text-[rgba(59,14,39,0.45)] uppercase tracking-[0.5px]">
              {customProblems.length} custom problem{customProblems.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
        <button
          onClick={handleRun}
          className="w-full h-[44px] font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-white bg-[#3B0E27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors"
        >
          Run
        </button>
      </div>

      {showCheatsheetModal && (
        <NewCheatsheetModal
          onClose={() => setShowCheatsheetModal(false)}
          onCreate={(title, content) => {
            setCheatsheet(title || "custom_cheatsheet");
          }}
          onBrowseShareBoard={() => {
            setShowCheatsheetModal(false);
            onNavigateToShareBoard?.();
          }}
          onViewShareItem={(itemId) => {
            setShowCheatsheetModal(false);
            onViewShareItem?.(itemId);
          }}
        />
      )}

      {showEditModal && cheatsheet !== "none" && (
        <EditCheatsheetModal
          cheatsheetKey={cheatsheet}
          onClose={() => setShowEditModal(false)}
          onSave={(key, title) => {
            // In a real app this would persist; for now just keep selection
          }}
        />
      )}

      {/* Share toast */}
      {shareToast && (
        <div
          className="fixed bottom-[28px] left-1/2 z-[999] flex items-center gap-[10px] px-[20px] py-[13px] bg-[#3b0e27]"
          style={{
            transform: "translateX(-50%)",
            boxShadow: "0 6px 24px rgba(59,14,39,0.25)",
            animation: "fadeInUp 0.2s ease",
          }}
        >
          <Share2 size={14} className="text-white shrink-0" />
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-white whitespace-nowrap">
            Shared to Share Board successfully
          </span>
          <button
            onClick={() => setShareToast(false)}
            className="bg-transparent border-none cursor-pointer p-0 ml-[8px] text-[rgba(255,255,255,0.5)] hover:text-white transition-colors"
          >
            <X size={13} />
          </button>
        </div>
      )}

      {/* Confirm Run Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowConfirmModal(false); }}
        >
          <div className="bg-white w-[440px] overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            {/* Header */}
            <div
              className="flex items-center justify-between px-[24px] h-[64px] shrink-0"
              style={{ borderBottom: '1px solid rgba(59,14,39,0.1)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[20px] text-[#3b0e27] leading-[32px]">
                Confirm Run
              </p>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="size-[30px] bg-[#F0EDEF] rounded-full flex items-center justify-center border-none cursor-pointer hover:bg-[#e4dae2] transition-colors shrink-0"
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 14 14">
                  <path d="M10.5 3.5L3.5 10.5" stroke="#3B0E27" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.5 3.5L10.5 10.5" stroke="#3B0E27" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Body rows */}
            <div className="flex flex-col gap-[20px] px-[24px] pt-[28px] pb-[24px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27]">
              <div className="flex items-center justify-between">
                <span style={{ opacity: 0.6 }}>Model</span>
                <span className="text-right">{selectedModel}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ opacity: 0.6 }}>Template</span>
                <span className="text-right capitalize">{template}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ opacity: 0.6 }}>Cheatsheet</span>
                <span className="text-right">{cheatsheet === "none" ? "None" : cheatsheet}</span>
              </div>
              <div className="flex items-center justify-between">
                <span style={{ opacity: 0.6 }}>Problems</span>
                <span className="text-right">{confirmProblemCount} problem{confirmProblemCount !== 1 ? "s" : ""}</span>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex items-center gap-[24px] px-[24px] pb-[24px]">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 h-[48px] font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-[#3b0e27] bg-white cursor-pointer hover:bg-[rgba(59,14,39,0.03)] transition-colors"
                style={{ border: '1px solid rgba(59,14,39,0.3)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRun}
                className="flex-1 h-[48px] font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-white bg-[#3b0e27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors"
              >
                Confirm &amp; Run
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Confirm Modal */}
      {showShareConfirmModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowShareConfirmModal(false); }}
        >
          <div className="bg-white w-[520px] overflow-hidden" style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            {/* Header */}
            <div
              className="flex items-center justify-between px-[24px] h-[64px] shrink-0"
              style={{ borderBottom: '1px solid rgba(59,14,39,0.1)' }}
            >
              <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[20px] text-[#3b0e27] leading-[32px]">
                Share to Share Board?
              </p>
              <button
                onClick={() => setShowShareConfirmModal(false)}
                className="size-[30px] bg-[#F0EDEF] rounded-full flex items-center justify-center border-none cursor-pointer hover:bg-[#e4dae2] transition-colors shrink-0"
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 14 14">
                  <path d="M10.5 3.5L3.5 10.5" stroke="#3B0E27" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3.5 3.5L10.5 10.5" stroke="#3B0E27" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-[24px] py-[28px]">
              <div className="flex flex-col gap-[16px] font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27] leading-[22px]">
                <p className="m-0">
                  Your cheatsheet will be published on Share Board and visible to all logged-in users.
                </p>
                <p className="m-0">
                  Other users will be able to see your avatar, full name, published timestamp, and cheatsheet content.
                </p>
                <p className="m-0">
                  Once published, the cheatsheet content cannot be edited. If you delete and republish it, a new published timestamp will be generated.
                </p>
              </div>
            </div>

            {/* Footer buttons */}
            <div className="flex items-center gap-[24px] px-[24px] pb-[24px]">
              <button
                onClick={() => setShowShareConfirmModal(false)}
                className="flex-1 h-[48px] font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-[#3b0e27] bg-white cursor-pointer hover:bg-[rgba(59,14,39,0.03)] transition-colors"
                style={{ border: '1px solid rgba(59,14,39,0.3)' }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowShareConfirmModal(false);
                  onNavigateToShareBoard?.();
                }}
                className="flex-1 h-[48px] font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-white bg-[#3b0e27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors"
              >
                Confirm &amp; Publish
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Result Card (redesigned to match Figma) ─────────────────────── */

function ResultCard({ result, expanded, onToggle }: { result: RunResult; expanded: boolean; onToggle: () => void }) {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const badgeLabel = result.error ? "ERROR" : result.correct ? "CORRECT" : "INCORRECT";
  const badgeBg = result.error ? "#fce4ec" : result.correct ? "#e8f5e9" : "#fce4ec";
  const badgeColor = result.error ? "#c62828" : result.correct ? "#2d8a4e" : "#c62828";
  const badgeBorder = result.error ? "#c62828" : result.correct ? "#2d8a4e" : "#c62828";

  const eq1 = result.equation.includes(" → ") ? result.equation.split(" → ")[0] : result.equation;
  const eq2 = result.equation.includes(" → ") ? result.equation.split(" → ")[1] : "";

  return (
    <div
      className="bg-white flex flex-col overflow-hidden"
      style={{
        border: '1px solid #3b0e2714',
      }}
    >
      {/* Header: model + badge */}
      <div className="px-[18px] pt-[14px] pb-[4px] flex items-center justify-between">
        <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[#3b0e27] tracking-[-0.3px] truncate flex-1 min-w-0 text-[15px]">
          {result.model}
        </p>
        <span
          className="px-[8px] py-[3px] font-['Helvetica_Neue:Medium',sans-serif] text-[11px] tracking-[0.3px] uppercase shrink-0 ml-[10px]"
          style={{ backgroundColor: badgeBg, color: badgeColor, border: `1px solid ${badgeBorder}` }}
        >
          {badgeLabel}
        </span>
      </div>

      {/* Problem label */}
      <div className="px-[18px] pb-[6px]">
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] text-[rgba(59,14,39,0.4)]">
          {result.problemLabel}
        </p>
      </div>

      {/* Equation boxes */}
      <div className="px-[18px] pt-[0px] pb-[10px]">
        <div className="flex items-center gap-[8px]">
          <div className="flex-1 min-w-0 px-[9px] py-[8px] bg-white" style={{ border: '1px solid #3b0e2714' }}>
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[#3b0e27] leading-[15.4px] break-all m-0 text-[12px]">
              {eq1}
            </p>
          </div>
          {eq2 && (
            <>
              <div className="shrink-0 px-[2px]">
                <svg width="12" height="7" viewBox="0 0 8.75 8.25" fill="none">
                  <path d="M0.625 4.125H8.125M4.625 0.625L8.125 4.125L4.625 7.625" stroke="#3B0E27" strokeOpacity="0.3" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="flex-1 min-w-0 px-[9px] py-[8px] bg-white" style={{ border: '1px solid #3b0e2714' }}>
                <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] text-[#3b0e27] leading-[15.4px] break-all m-0">
                  {eq2}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Output / Expected */}
      <div className="px-[18px] pb-[8px]">
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[11px] text-[rgba(59,14,39,0.6)] opacity-60 leading-[15.4px] flex items-center gap-[10px]">
          <span>Output: {result.output}</span>
          <span style={{ width: '1px', height: '10px', backgroundColor: 'rgba(59,14,39,0.2)', display: 'inline-block', flexShrink: 0 }} />
          <span>Expected: {result.expected}</span>
        </p>
      </div>

      {/* Stats row — 4 equal columns */}
      <div className="mx-[18px] mb-[10px] bg-[rgba(59,14,39,0.02)] flex" style={{ border: '1px solid #3b0e270f' }}>
        {[
          { value: result.error ? "--" : `${result.time}s`, label: "TIME" },
          { value: result.error ? "--" : `$${result.cost}`, label: "COST" },
          { value: result.error ? "--" : `${result.tokensIn}`, label: "IN" },
          { value: result.error ? "--" : `${result.tokensOut}`, label: "OUT" },
        ].map((stat, i) => (
          <div
            key={stat.label}
            className="flex-1 flex flex-col items-center py-[8px]"
            style={i > 0 ? { borderLeft: '1px solid #3b0e270f' } : undefined}
          >
            <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[#3b0e27] text-[14px]">
              {(!result.correct && !result.error && (stat.label === "COST" || stat.label === "IN" || stat.label === "OUT")) ? "--" : stat.value}
            </span>
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[rgba(59,14,39,0.35)] uppercase tracking-[0.3px] mt-[1px] text-[10px]">{stat.label}</span>
          </div>
        ))}
      </div>

      {/* Action bar: Config | Show prompt | Show response */}
      <div className="flex items-center h-[36px]" style={{ borderTop: '1px solid #3b0e270f' }}>
        <button
          onClick={() => setShowConfig(!showConfig)}
          className="flex-1 flex items-center justify-center gap-[4px] h-full bg-transparent border-none cursor-pointer"
        >
          <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-[#3b0e27]">Config</span>
          <ChevronDown size={14} className="text-[rgba(59,14,39,0.3)]" style={{ transform: showConfig ? 'rotate(180deg)' : undefined }} />
        </button>
        <div className="w-px h-[20px] bg-[rgba(59,14,39,0.08)]" />
        <button
          onClick={() => setShowPrompt(!showPrompt)}
          className="flex-1 flex items-center justify-center gap-[4px] h-full bg-transparent border-none cursor-pointer"
        >
          <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-[#3b0e27]">Show prompt</span>
          <ChevronDown size={14} className="text-[rgba(59,14,39,0.3)]" style={{ transform: showPrompt ? 'rotate(180deg)' : undefined }} />
        </button>
        <div className="w-px h-[20px] bg-[rgba(59,14,39,0.08)]" />
        <button
          onClick={onToggle}
          className="flex-1 flex items-center justify-center gap-[4px] h-full bg-transparent border-none cursor-pointer"
        >
          <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-[#3b0e27]">Show{`  `}response</span>
          <ChevronDown size={14} className="text-[rgba(59,14,39,0.3)]" style={{ transform: expanded ? 'rotate(180deg)' : undefined }} />
        </button>
      </div>

      {/* Expandable sections */}
      {showConfig && (
        <div className="px-[18px] py-[12px] bg-[rgba(59,14,39,0.02)]" style={{ borderTop: '1px solid #3b0e270f' }}>
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)] leading-[18px] m-0">
            Template: {result.config} · Cheatsheet: {result.cheatsheet === "none" ? "none" : result.cheatsheet}
          </p>
        </div>
      )}
      {showPrompt && (
        <div className="px-[18px] py-[12px] bg-[rgba(59,14,39,0.02)]" style={{ borderTop: '1px solid #3b0e270f' }}>
          <pre className="font-['Menlo',monospace] text-[11px] text-[rgba(59,14,39,0.5)] leading-[18px] whitespace-pre-wrap break-all m-0">
            {result.prompt}
          </pre>
        </div>
      )}
      {expanded && (
        <div className="px-[18px] py-[12px] bg-[rgba(59,14,39,0.02)]" style={{ borderTop: '1px solid #3b0e270f' }}>
          <pre className="font-['Menlo',monospace] text-[11px] text-[rgba(59,14,39,0.5)] leading-[18px] whitespace-pre-wrap break-all m-0">
            {result.response}
          </pre>
        </div>
      )}
    </div>
  );
}

/* ── Run Results Tab ─────────────────────���───────────────────────── */

function RunResultsView({ run, creditsUsed, creditsTotal }: { run: RunHistory; creditsUsed: number; creditsTotal: number }) {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [filterProblem, setFilterProblem] = useState("");

  const toggleCard = useCallback((id: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const filteredResults = useMemo(() => {
    let r = run.results;
    if (filterProblem) r = r.filter((x) => x.problem === filterProblem);
    return r;
  }, [run.results, filterProblem]);

  const totalAccuracy = run.summary.length
    ? run.summary.reduce((s, r) => s + r.accuracy * r.count, 0) / run.summary.reduce((s, r) => s + r.count, 0)
    : 0;

  return (
    <div className="flex flex-col gap-[20px] p-[28px] overflow-y-auto flex-1">
      {/* Status bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-[12px]">
          <span className={`px-[12px] py-[4px] font-['Helvetica_Neue:Medium',sans-serif] text-[11px] tracking-[0.5px] uppercase ${
            run.status === "DONE" ? "bg-[#e8f5e9] text-[#2d8a4e]" : "bg-[#fce4ec] text-[#c62828]"
          }`} style={{ border: `1px solid ${run.status === "DONE" ? "#2d8a4e" : "#c62828"}` }}>
            {run.status}
          </span>
          <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[rgba(59,14,39,0.5)] text-[#3b0e27]">
            {run.progress}
          </span>
        </div>
        <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[rgba(59,14,39,0.4)]">
          {creditsUsed} / {creditsTotal} Credits{`  `}Reset at 00:00 UTC
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full h-[4px] bg-[#e8f5e9]">
        <div className="h-full bg-[#2d8a4e]" style={{ width: run.status === "DONE" ? "100%" : "0%" }} />
      </div>

      {/* Summary Table */}
      <div className="w-full" style={{ border: '1px solid #3b0e2714' }}>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-[rgba(59,14,39,0.03)]">
              {["Model", "Template", "Cheatsheet", "Count", "Accuracy", "Avg Cost", "Avg Time"].map((h) => (
                <th
                  key={h}
                  className="px-[14px] py-[10px] text-left font-['Helvetica_Neue:Medium',sans-serif] text-[11px] text-[rgba(59,14,39,0.5)] tracking-[0.2px]"
                  style={{ borderBottom: '1px solid #3b0e2714' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {run.summary.map((row, i) => (
              <tr key={i} className="last:border-b-0" style={{ borderBottom: '1px solid #3b0e270d' }}>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27]">{row.model}</td>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.template}</td>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.cheatsheet}</td>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.count}</td>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-[#2d8a4e]">{row.accuracy.toFixed(1)}%</td>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">${row.avgCost}</td>
                <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.avgTime}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Filter */}
      <div className="flex items-center">
        <div className="relative">
          <select
            value={filterProblem}
            onChange={(e) => setFilterProblem(e.target.value)}
            className="appearance-none h-[32px] pl-[12px] pr-[28px] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] cursor-pointer focus:outline-none"
            style={{ border: '1px solid #3b0e271f' }}
          >
            <option value="">All Problems</option>
            {[...new Set(run.results.map((r) => r.problem))].map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
        </div>
      </div>

      {/* Result Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
        {filteredResults.map((r) => (
          <ResultCard
            key={r.id}
            result={r}
            expanded={expandedCards.has(r.id)}
            onToggle={() => toggleCard(r.id)}
          />
        ))}
      </div>

      {filteredResults.length === 0 && (
        <div className="py-[60px] flex items-center justify-center">
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[rgba(59,14,39,0.3)]">
            No results match the current filters.
          </p>
        </div>
      )}
    </div>
  );
}

/* ── History Tab ──────────────────────────────────────────────────── */

function HistoryView({ runs, onSelectRun }: { runs: RunHistory[]; onSelectRun: (id: string) => void }) {
  const [expandedRun, setExpandedRun] = useState<string | null>(runs.length > 0 ? runs[0].id : null);
  const [filterTemplate, setFilterTemplate] = useState("");
  const [filterModel, setFilterModel] = useState("");
  const [filterProblem, setFilterProblem] = useState("");

  const allTemplates = Array.from(new Set(runs.map((r) => r.template).filter(Boolean)));
  const allModels = Array.from(new Set(runs.flatMap((r) => r.models).filter(Boolean)));
  const allProblems = Array.from(new Set(runs.flatMap((r) => r.results.map((res) => res.problem)).filter(Boolean)));

  const filteredRuns = runs.filter((r) => {
    if (filterTemplate && r.template !== filterTemplate) return false;
    if (filterModel && !r.models.includes(filterModel)) return false;
    if (filterProblem && !r.results.some((res) => res.problem === filterProblem)) return false;
    return true;
  });

  const totalTasks = runs.length;
  const tasks24h = runs.filter((r) => {
    const d = new Date(r.timestamp);
    return Date.now() - d.getTime() < 86400000;
  }).length;
  const creditsUsed = runs.reduce((s, r) => s + r.results.length * 3, 0);
  const creditsLeft = Math.max(0, 200 - creditsUsed);
  const cheatsheets = new Set(runs.map((r) => r.cheatsheet)).size || 0;

  const statsData = [
    { value: creditsLeft, label: "CREDITS LEFT" },
    { value: creditsUsed, label: "CREDITS USED" },
    { value: totalTasks, label: "TOTAL TASKS" },
    { value: tasks24h, label: "TASKS (24H)" },
    { value: cheatsheets, label: "CHEATSHEETS" },
  ];

  return (
    <div className="flex flex-col gap-[4px] p-[28px] overflow-y-auto flex-1">
      

      {/* Stats Panel */}
      <div className="grid grid-cols-3 md:grid-cols-5 gap-[10px] mb-[20px]">
        {statsData.map((s) => (
          <div
            key={s.label}
            className="flex flex-col items-center justify-center py-[14px] px-[8px] bg-white"
            style={{ border: '1px solid rgba(59,14,39,0.1)' }}
          >
            <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[22px] text-[#3b0e27] tracking-[-0.3px]">
              {s.value}
            </span>
            <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[10px] text-[rgba(59,14,39,0.4)] tracking-[0.6px] mt-[4px]">
              {s.label}
            </span>
          </div>
        ))}
      </div>

      {/* Operation History header + filters */}
      <div className="flex items-center justify-between mb-[12px] mt-[4px]">
        <div className="flex items-center gap-[10px]">
          <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[18px] text-[#3b0e27] tracking-[-0.3px]">
            Operation History
          </span>
          <span
            className="px-[8px] py-[2px] font-['Helvetica_Neue:Medium',sans-serif] text-[11px] text-[#3b0e27]"
            style={{ border: '1px solid rgba(59,14,39,0.2)' }}
          >
            {filteredRuns.length} Results
          </span>
        </div>
        <div className="flex items-center gap-[8px]">
          {/* All Templates */}
          <div className="relative">
            <select
              value={filterTemplate}
              onChange={(e) => setFilterTemplate(e.target.value)}
              className="appearance-none pl-[10px] pr-[24px] py-[5px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] bg-white cursor-pointer outline-none"
              style={{ border: '1px solid rgba(59,14,39,0.15)' }}
            >
              <option value="">All Templates</option>
              {allTemplates.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <svg className="absolute right-[7px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="12" fill="none" viewBox="0 0 12 12">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="#3B0E27" strokeOpacity="0.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {/* All Models */}
          <div className="relative">
            <select
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              className="appearance-none pl-[10px] pr-[24px] py-[5px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] bg-white cursor-pointer outline-none"
              style={{ border: '1px solid rgba(59,14,39,0.15)' }}
            >
              <option value="">All Models</option>
              {allModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <svg className="absolute right-[7px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="12" fill="none" viewBox="0 0 12 12">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="#3B0E27" strokeOpacity="0.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          {/* All Problems */}
          <div className="relative">
            <select
              value={filterProblem}
              onChange={(e) => setFilterProblem(e.target.value)}
              className="appearance-none pl-[10px] pr-[24px] py-[5px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] bg-white cursor-pointer outline-none"
              style={{ border: '1px solid rgba(59,14,39,0.15)' }}
            >
              <option value="">All Problems</option>
              {allProblems.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <svg className="absolute right-[7px] top-1/2 -translate-y-1/2 pointer-events-none" width="12" height="12" fill="none" viewBox="0 0 12 12">
              <path d="M3 4.5L6 7.5L9 4.5" stroke="#3B0E27" strokeOpacity="0.3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </div>

      {runs.length === 0 && (
        <div className="py-[60px] flex items-center justify-center">
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[rgba(59,14,39,0.3)]">
            No runs yet. Configure and click Run to get started.
          </p>
        </div>
      )}

      {filteredRuns.map((run) => {
        const isExpanded = expandedRun === run.id;
        const totalAccuracy = run.summary.length
          ? run.summary.reduce((s, r) => s + r.accuracy * r.count, 0) / run.summary.reduce((s, r) => s + r.count, 0)
          : 0;

        return (
          <div key={run.id} className="border border-[rgba(59,14,39,0.08)] bg-white mb-[12px]">
            {/* Run header */}
            <button
              onClick={() => setExpandedRun(isExpanded ? null : run.id)}
              className="w-full flex items-center justify-between px-[20px] py-[14px] bg-transparent border-none cursor-pointer text-left"
            >
              <div className="flex items-center gap-[12px] flex-1 min-w-0">
                <span className={`px-[10px] py-[3px] font-['Helvetica_Neue:Medium',sans-serif] text-[10px] tracking-[0.5px] uppercase shrink-0 ${
                  run.status === "DONE" ? "bg-[#e8f5e9] text-[#2d8a4e] border border-[#2d8a4e]" : "bg-[#fce4ec] text-[#c62828] border border-[#c62828]"
                }`}>
                  {run.status}
                </span>
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] text-[#3b0e27] truncate">
                  {run.models.join(", ")}
                </span>
              </div>
              <div className="flex items-center gap-[12px] shrink-0 ml-[12px]">
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27]">
                  {run.progress}
                </span>
                <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.35)]">
                  {run.timestamp}
                </span>
                <svg width="14" height="14" fill="none" viewBox="0 0 14 14">
                  <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="#3B0E27" strokeOpacity="0.3" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.16667" />
                </svg>
              </div>
            </button>

            {isExpanded && (
              <div className="px-[20px] pb-[20px] flex flex-col gap-[16px]">
                {/* Status + Progress */}
                

                {/* Summary table */}
                <div className="w-full border border-[rgba(59,14,39,0.08)]">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-[rgba(59,14,39,0.03)]">
                        {["Model", "Template", "Cheatsheet", "Count", "Accuracy", "Avg Cost", "Avg Time"].map((h) => (
                          <th key={h} className="px-[14px] py-[10px] text-left font-['Helvetica_Neue:Medium',sans-serif] text-[11px] text-[rgba(59,14,39,0.5)] tracking-[0.2px] border-b border-[rgba(59,14,39,0.08)]">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {run.summary.map((row, i) => (
                        <tr key={i} className="border-b border-[rgba(59,14,39,0.05)]">
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27]">{row.model}</td>
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.template}</td>
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.cheatsheet}</td>
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.count}</td>
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Medium',sans-serif] text-[12px] text-[#2d8a4e]">{row.accuracy.toFixed(1)}%</td>
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">${row.avgCost}</td>
                          <td className="px-[14px] py-[9px] font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[rgba(59,14,39,0.5)]">{row.avgTime}</td>
                        </tr>
                      ))}
                      <tr className="bg-[rgba(59,14,39,0.02)] border-t border-[rgba(59,14,39,0.1)]">
                        
                        
                        
                        
                        
                        
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* Filter */}
                <div className="flex items-center gap-[10px] flex-wrap">
                  
                </div>

                {/* Result cards grid — 2 columns matching Run view */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
                  {run.results.slice(0, 8).map((r) => (
                    <ResultCard key={r.id} result={r} expanded={false} onToggle={() => {}} />
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── All Tasks Tab ───────────────────────────────────────────────── */

function AllTasksView({ runs }: { runs: RunHistory[] }) {
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [filterModel, setFilterModel] = useState("");
  const [filterProblem, setFilterProblem] = useState("");
  const [searchResults, setSearchResults] = useState("");

  const toggleCard = useCallback((id: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const allResults = useMemo(() => runs.flatMap((run) => run.results), [runs]);

  const filteredResults = useMemo(() => {
    let r = allResults;
    if (filterModel) r = r.filter((x) => x.model === filterModel);
    if (filterProblem) r = r.filter((x) => x.problem === filterProblem);
    if (searchResults) {
      const query = searchResults.toLowerCase();
      r = r.filter(
        (x) =>
          x.model.toLowerCase().includes(query) ||
          x.equation.toLowerCase().includes(query) ||
          x.verdict.toLowerCase().includes(query)
      );
    }
    return r;
  }, [allResults, filterModel, filterProblem, searchResults]);

  const uniqueModels = [...new Set(allResults.map((r) => r.model))];
  const uniqueProblems = [...new Set(allResults.map((r) => r.problem))];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Top Filter Bar */}
      <div className="shrink-0 px-[16px] md:px-[28px] py-[16px] bg-white border-b border-[rgba(59,14,39,0.06)] flex items-center gap-[12px] flex-wrap">
        <span className="font-['Helvetica_Neue:Medium',sans-serif] text-[18px] text-[#3b0e27] tracking-[-0.4px] mr-[8px]">
          All Tasks
        </span>
        <span className="px-[8px] py-[2px] bg-[rgba(59,14,39,0.05)] text-[rgba(59,14,39,0.4)] font-['Helvetica_Neue:Medium',sans-serif] text-[11px]">
          {filteredResults.length} Results
        </span>
        <div className="flex-1" />
        <div className="flex items-center gap-[8px]">
          <div className="relative">
            <select
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value)}
              className="appearance-none h-[32px] pl-[12px] pr-[28px] border border-[rgba(59,14,39,0.1)] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] cursor-pointer focus:outline-none min-w-[110px]"
            >
              <option value="">All Models</option>
              {uniqueModels.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
          </div>
          <div className="relative">
            <select
              value={filterProblem}
              onChange={(e) => setFilterProblem(e.target.value)}
              className="appearance-none h-[32px] pl-[12px] pr-[28px] border border-[rgba(59,14,39,0.1)] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] cursor-pointer focus:outline-none min-w-[110px]"
            >
              <option value="">All Problems</option>
              {uniqueProblems.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <ChevronDown size={12} className="absolute right-[10px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.3)] pointer-events-none" />
          </div>
          <div className="relative min-w-[180px]">
            <Search size={13} className="absolute left-[10px] top-1/2 -translate-y-1/2 text-[rgba(59,14,39,0.25)]" />
            <input
              type="text"
              value={searchResults}
              onChange={(e) => setSearchResults(e.target.value)}
              placeholder="Search..."
              className="w-full h-[32px] pl-[30px] pr-[10px] border border-[rgba(59,14,39,0.1)] bg-white font-['Helvetica_Neue:Regular',sans-serif] text-[12px] text-[#3b0e27] focus:outline-none"
            />
          </div>
          {(filterModel || filterProblem || searchResults) && (
            <button
              onClick={() => { setFilterModel(""); setFilterProblem(""); setSearchResults(""); }}
              className="flex items-center justify-center size-[32px] bg-[rgba(59,14,39,0.05)] text-[rgba(59,14,39,0.4)] hover:bg-[rgba(59,14,39,0.1)] transition-colors border-none cursor-pointer"
            >
              <RotateCcw size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-[16px] md:px-[28px] py-[24px]">
        {filteredResults.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
            {filteredResults.map((r) => (
              <ResultCard key={r.id} result={r} expanded={expandedCards.has(r.id)} onToggle={() => toggleCard(r.id)} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full opacity-40">
            <Search size={40} className="mb-[12px] text-[rgba(59,14,39,0.15)]" />
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[#3b0e27]">
              No results match your filters.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Cheatsheets Tab (Right Panel) ────────────────────────────────── */

interface CheatsheetCardData {
  id: string;
  title: string;
  content: string;
  createdAt: string;
  author?: string;
  sharedAt?: string;
  authorAvatar?: string;
  referenceCount?: number;
  favoriteCount?: number;
}

const MY_CHEATSHEETS_DATA: CheatsheetCardData[] = [
  {
    id: "mycs-1",
    title: "cheatsheet_proof_first",
    content: "Strategy: Always attempt a direct proof before constructing a counterexample.\n1. Identify the universal quantifiers in both equations.\n2. Try substitution of specific elements to reduce to a known identity.\n3. Use transitivity of equality to chain intermediate steps.\n4. If proof fails after 3 attempts, pivot to counterexample search.",
    createdAt: "2026-03-14T10:00:00Z",
    author: "Alice Johnson",
    authorAvatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    referenceCount: 8,
    favoriteCount: 3,
  },
  {
    id: "mycs-2",
    title: "cheatsheet_fast_filters_micro",
    content: "Fast-filter heuristics for magma implication:\n• Check arity mismatch — if lhs/rhs depths differ by >2, likely FALSE.\n• Test on the two-element magma first (cheapest falsification).\n• Apply idempotency filter: if A is idempotent and B is not, FALSE.\n• Use commutativity shortcut when both sides are symmetric.",
    createdAt: "2026-03-12T08:30:00Z",
    author: "Alice Johnson",
    authorAvatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    likeCount: 5,
    favoriteCount: 2,
  },
  {
    id: "mycs-3",
    title: "cheatsheet_counterexample_first",
    content: "Counterexample-first approach:\n1. Enumerate magmas of size 2, then 3.\n2. For each magma M, evaluate LHS and RHS under all assignments.\n3. If LHS ≠ RHS for any assignment → output FALSE with the witness.\n4. If no counterexample found up to size 4 → attempt formal proof.",
    createdAt: "2026-03-10T16:45:00Z",
    author: "Alice Johnson",
    authorAvatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    likeCount: 12,
    favoriteCount: 6,
  },
  {
    id: "mycs-4",
    title: "cheatsheet_balanced_hybrid",
    content: "Balanced hybrid strategy:\n1. Quick arity & symmetry pre-check (< 1s)\n2. Small model enumeration |M| ≤ 3\n3. If no counterexample, attempt substitution proof\n4. Fallback: request extended reasoning with CoT",
    createdAt: "2026-03-08T11:20:00Z",
    author: "Alice Johnson",
    authorAvatar: "https://images.unsplash.com/photo-1649589244330-09ca58e4fa64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB3b21hbiUyMHBvcnRyYWl0fGVufDF8fHx8MTc3MzY3NzI5Nnww&ixlib=rb-4.1.0&q=80&w=1080",
    likeCount: 7,
    favoriteCount: 4,
  },
];

function formatCsDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function truncateCsContent(text: string, maxLines: number): { text: string; truncated: boolean } {
  const lines = text.split("\n");
  if (lines.length <= maxLines) return { text, truncated: false };
  return { text: lines.slice(0, maxLines).join("\n"), truncated: true };
}

/* ── Cheatsheet Detail Modal ────────────────────────────────────────── */

function CheatsheetDetailModal({ item, isFavorite, onClose }: { item: CheatsheetCardData; isFavorite?: boolean; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
    >
      <div
        className="bg-white w-[720px] max-w-[90vw] flex flex-col"
        style={{ height: "680px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-[24px] py-[20px] shrink-0"
          style={{ borderBottom: "1px solid rgba(59,14,39,0.1)" }}
        >
          <div className="flex flex-col gap-[8px]">
            <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[18px] text-[#3b0e27] m-0">
              {item.title}
            </p>
            <div className="flex items-center gap-[12px]">
              {isFavorite && item.author && (
                <div className="flex items-center gap-[6px]">
                  <Star size={12} fill="#3B0E27" stroke="#3B0E27" className="shrink-0" />
                  <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]" style={{ color: "rgba(59,14,39,0.5)" }}>
                    by {item.author}
                  </span>
                </div>
              )}
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0" style={{ color: "rgba(59,14,39,0.4)" }}>
                {isFavorite && item.sharedAt ? `Published ${formatCsDate(item.sharedAt)}` : `Created ${formatCsDate(item.createdAt)}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer p-[4px] shrink-0"
          >
            <X size={18} style={{ color: "rgba(59,14,39,0.4)" }} />
          </button>
        </div>

        {/* Content */}
        <div className="px-[24px] py-[20px] flex flex-col gap-[12px] overflow-y-auto flex-1 min-h-0">
          <div className="flex items-center gap-[6px]">
            <FileText size={14} style={{ color: "rgba(59,14,39,0.4)" }} />
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px] m-0" style={{ color: "rgba(59,14,39,0.6)" }}>
              Cheatsheet Content
            </p>
          </div>
          <div
            className="p-[16px] font-['Menlo',monospace] text-[13px] whitespace-pre-wrap break-words leading-[1.7] flex-1 min-h-0 overflow-y-auto"
            style={{
              backgroundColor: "rgba(59,14,39,0.02)",
              border: "1px solid rgba(59,14,39,0.1)",
              color: "#3b0e27",
            }}
          >
            {item.content}
          </div>
        </div>
      </div>
    </div>
  );
}

function CheatsheetCard({ item, isFavorite, onClick }: { item: CheatsheetCardData; isFavorite?: boolean; onClick?: () => void }) {
  const preview = truncateCsContent(item.content, 5);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(isFavorite || false);
  const [localLikeCount, setLocalLikeCount] = useState(item.likeCount || 0);
  const [localFavoriteCount, setLocalFavoriteCount] = useState(item.favoriteCount || 0);

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLiked(!liked);
    setLocalLikeCount(prev => liked ? prev - 1 : prev + 1);
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSaved(!saved);
    setLocalFavoriteCount(prev => saved ? prev - 1 : prev + 1);
  };

  return (
    <div
      className="flex flex-col bg-white transition-colors hover:bg-[rgba(59,14,39,0.02)] cursor-pointer"
      style={{ border: "1px solid rgba(59,14,39,0.1)" }}
      onClick={onClick}
    >
      {/* Header */}
      <div className="px-[20px] pt-[18px] pb-[12px] flex flex-col gap-[8px]">
        {/* Top row: publisher info + date */}
        <div className="flex items-center justify-between gap-[12px]">
          <div className="flex items-center gap-[8px]">
            {item.authorAvatar ? (
              <img
                src={item.authorAvatar}
                alt={item.author || "User"}
                className="w-[20px] h-[20px] rounded-full object-cover shrink-0"
                style={{ border: "1px solid rgba(59,14,39,0.1)" }}
              />
            ) : (
              <div
                className="w-[20px] h-[20px] rounded-full flex items-center justify-center shrink-0 font-['Helvetica_Neue:Regular',sans-serif] text-[10px]"
                style={{
                  backgroundColor: "rgba(59,14,39,0.04)",
                  border: "1px solid rgba(59,14,39,0.1)",
                  color: "rgba(59,14,39,0.5)",
                }}
              >
                {(item.author || "U").charAt(0).toUpperCase()}
              </div>
            )}
            <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0" style={{ color: "rgba(59,14,39,0.5)" }}>
              {item.author || "Unknown"}
            </p>
          </div>
          <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[12px] m-0 whitespace-nowrap shrink-0" style={{ color: "rgba(59,14,39,0.35)" }}>
            {formatCsDate(isFavorite && item.sharedAt ? item.sharedAt : item.createdAt)}
          </p>
        </div>

        {/* Title */}
        <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[16px] m-0" style={{ color: "#3b0e27" }}>
          {item.title}
        </p>
      </div>

      {/* Content preview */}
      <div className="px-[20px] py-[0px]">
        <div
          className="p-[14px] font-['Menlo',monospace] whitespace-pre-wrap break-words leading-[1.65] text-[14px] h-[166px] overflow-hidden"
          style={{
            backgroundColor: "rgba(59,14,39,0.02)",
            border: "1px solid rgba(59,14,39,0.06)",
            color: "rgba(59,14,39,0.6)",
          }}
        >
          {preview.text}
          {preview.truncated && (
            <span style={{ color: "rgba(59,14,39,0.3)" }}>{"\n..."}</span>
          )}
        </div>
      </div>

      {/* Actions row */}
      <div className="px-[20px] py-[10px] flex items-center justify-between mt-auto">
        <div className="flex items-center gap-[8px]">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick?.();
            }}
            className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[4px_0]"
            style={{ color: "rgba(59,14,39,0.5)" }}
          >
            <Eye size={14} />
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">View Details</span>
          </button>
        </div>
        <div className="flex items-center gap-[2px]">
          {/* Reference button */}
          <button
            onClick={handleLike}
            className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[6px_10px] transition-all"
            style={{ color: liked ? "#3B0E27" : "rgba(59,14,39,0.4)" }}
          >
            <Repeat2
              size={14}
              fill={liked ? "#3B0E27" : "none"}
              stroke={liked ? "#3B0E27" : "currentColor"}
              strokeWidth={1.8}
            />
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">{localLikeCount}</span>
          </button>
          {/* Favorite button */}
          <button
            onClick={handleFavorite}
            className="flex items-center gap-[6px] bg-transparent border-none cursor-pointer p-[6px_10px] transition-all"
            style={{ color: saved ? "#3B0E27" : "rgba(59,14,39,0.4)" }}
          >
            <Star
              size={14}
              fill={saved ? "#3B0E27" : "none"}
              stroke={saved ? "#3B0E27" : "currentColor"}
              strokeWidth={1.8}
            />
            <span className="font-['Helvetica_Neue:Regular',sans-serif] text-[13px]">{localFavoriteCount}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

type CheatsheetsSubTab = "mine" | "favorites";

function CheatsheetsView({
  favoriteIds,
  onNavigateToShareBoard,
  onViewShareItem,
}: {
  favoriteIds: Set<string>;
  onNavigateToShareBoard?: () => void;
  onViewShareItem?: (itemId: string) => void;
}) {
  const [subTab, setSubTab] = useState<CheatsheetsSubTab>("mine");
  const [detailItem, setDetailItem] = useState<CheatsheetCardData | null>(null);

  // Build favorites data from SOURCE_MY_FAVORITES that are marked as favorited
  const myFavorites: CheatsheetCardData[] = useMemo(() => {
    return SOURCE_MY_FAVORITES
      .filter((f) => favoriteIds.has(f.value))
      .map((f) => ({
        id: f.value,
        title: f.label,
        content: FAVORITE_PREFILL[f.value]?.content ?? "",
        createdAt: f.publishedAt,
        author: f.author,
        sharedAt: f.publishedAt,
        authorAvatar: f.authorAvatar,
        likeCount: f.likeCount || 0,
        favoriteCount: f.favoriteCount || 0,
      }));
  }, [favoriteIds]);

  const SUB_TABS: { key: CheatsheetsSubTab; label: string }[] = [
    { key: "mine", label: "My Cheatsheets" },
    { key: "favorites", label: "My Favorites" },
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Sub-tab bar */}
      <div
        className="shrink-0 flex items-center px-[28px]"
        style={{ borderBottom: "1px solid rgba(59,14,39,0.08)" }}
      >
        {SUB_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className="px-[16px] py-[12px] font-['Helvetica_Neue:Medium',sans-serif] text-[13px] cursor-pointer border-b-2 border-x-0 border-t-0 bg-transparent transition-colors"
            style={{
              color: subTab === tab.key ? "#3b0e27" : "rgba(59,14,39,0.4)",
              borderBottomColor: subTab === tab.key ? "#3b0e27" : "transparent",
            }}
          >
            {tab.label}
            {tab.key === "favorites" && myFavorites.length > 0 && (
              <span
                className="ml-[6px] px-[5px] py-[1px] font-['Helvetica_Neue:Medium',sans-serif] text-[10px]"
                style={{
                  backgroundColor: subTab === "favorites" ? "rgba(59,14,39,0.08)" : "rgba(59,14,39,0.05)",
                  color: subTab === "favorites" ? "#3b0e27" : "rgba(59,14,39,0.4)",
                }}
              >
                {myFavorites.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-[28px]">
        {subTab === "mine" && (
          <>
            {MY_CHEATSHEETS_DATA.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[16px]">
                {MY_CHEATSHEETS_DATA.map((cs) => (
                  <CheatsheetCard key={cs.id} item={cs} onClick={() => setDetailItem(cs)} />
                ))}
              </div>
            ) : (
              /* Empty state — My Cheatsheets */
              <div className="flex flex-col items-center justify-center py-[80px] gap-[16px]">
                <div
                  className="flex items-center justify-center size-[56px]"
                  style={{ backgroundColor: "rgba(59,14,39,0.04)" }}
                >
                  <FileText size={26} style={{ color: "rgba(59,14,39,0.2)" }} />
                </div>
                <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-[#3b0e27] m-0 text-center">
                  No cheatsheets yet
                </p>
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0 text-center max-w-[320px] leading-[22px]"
                  style={{ color: "rgba(59,14,39,0.45)" }}
                >
                  Create a new cheatsheet from the left panel to get started.
                </p>
              </div>
            )}
          </>
        )}

        {subTab === "favorites" && (
          <>
            {myFavorites.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-[16px]">
                {myFavorites.map((cs) => (
                  <CheatsheetCard key={cs.id} item={cs} isFavorite onClick={() => setDetailItem(cs)} />
                ))}
              </div>
            ) : (
              /* Empty state — My Favorites */
              <div className="flex flex-col items-center justify-center py-[80px] gap-[16px]">
                <div
                  className="flex items-center justify-center size-[56px]"
                  style={{ backgroundColor: "rgba(59,14,39,0.04)" }}
                >
                  <Star size={26} style={{ color: "rgba(59,14,39,0.2)" }} />
                </div>
                <p className="font-['Helvetica_Neue:Medium',sans-serif] text-[16px] text-[#3b0e27] m-0 text-center">
                  No saved cheatsheets yet
                </p>
                <p
                  className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] m-0 text-center max-w-[360px] leading-[22px]"
                  style={{ color: "rgba(59,14,39,0.45)" }}
                >
                  No saved cheatsheets yet! Browse Share Board to discover public cheatsheets.
                </p>
                <button
                  onClick={() => onNavigateToShareBoard?.()}
                  className="mt-[8px] px-[24px] h-[40px] font-['Helvetica_Neue:Medium',sans-serif] text-[14px] text-white bg-[#3B0E27] border-none cursor-pointer hover:bg-[#4D1233] transition-colors flex items-center gap-[8px]"
                >
                  <BookOpen size={15} />
                  Open Share Board
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {detailItem && (
        <CheatsheetDetailModal
          item={detailItem}
          isFavorite={subTab === "favorites"}
          onClose={() => setDetailItem(null)}
        />
      )}
    </div>
  );
}

/* ── Initial Mock Data ────────────────────────────────────────────── */

const INITIAL_RUNS: RunHistory[] = [
  (() => {
    const mods = ["openai/gpt-5-mini"];
    const pids = ["normal#6", "normal#8", "normal#3", "normal#9"];
    const results = generateMockResults(mods, "basic", "none", pids);
    const summary = generateSummary(mods, "basic", "none", results);
    return {
      id: "run-001",
      name: "Run 1",
      timestamp: "2026/2/28 09:50:30",
      models: mods,
      template: "basic",
      cheatsheet: "none",
      problemCount: pids.length,
      status: "DONE" as const,
      progress: `${results.length} / ${results.length} (100%)`,
      accuracy: summary.length ? summary.reduce((s, r) => s + r.accuracy, 0) / summary.length : 0,
      totalCost: results.reduce((s, r) => s + r.cost, 0),
      totalTime: results.reduce((s, r) => s + r.time, 0).toFixed(1) + "s",
      results,
      summary,
    };
  })(),
  (() => {
    const mods = ["deepseek/deepseek-v3.2"];
    const pids = ["normal#1", "normal#2", "normal#3", "normal#4"];
    const results = generateMockResults(mods, "compact-json", "cs_5e566dcb-7325-402a-9f5a-ef767c89e1bb", pids);
    const summary = generateSummary(mods, "compact-json", "cs_5e566dcb-7325-402a-9f5a-ef767c89e1bb", results);
    return {
      id: "run-002",
      name: "Run 2",
      timestamp: "2026/2/28 09:50:30",
      models: mods,
      template: "compact-json",
      cheatsheet: "cs_5e566dcb-7325-402a-9f5a-ef767c89e1bb",
      problemCount: pids.length,
      status: "DONE" as const,
      progress: `${results.length} / ${results.length} (100%)`,
      accuracy: summary.length ? summary.reduce((s, r) => s + r.accuracy, 0) / summary.length : 0,
      totalCost: results.reduce((s, r) => s + r.cost, 0),
      totalTime: results.reduce((s, r) => s + r.time, 0).toFixed(1) + "s",
      results,
      summary,
    };
  })(),
  (() => {
    const mods = ["deepseek/deepseek-chat"];
    const pids = ["hard#0"];
    const failResult: RunResult = {
      id: "fail-001",
      model: "deepseek/deepseek-chat",
      config: "basic",
      cheatsheet: "none",
      problem: "hard#0",
      problemLabel: "hard#0",
      equation: "y * (y * ((x * y) * x)) → x * y = ((z * x) * ...",
      answer: "FALSE",
      correct: false,
      error: true,
      time: 0,
      cost: 0,
      tokensIn: 0,
      tokensOut: 0,
      output: "--",
      expected: "FALSE",
      verdict: "ERROR: API timeout",
      response: "Error: Connection timeout",
      prompt: "",
    };
    return {
      id: "run-003",
      name: "Run 3",
      timestamp: "2026/2/28 12:30:27",
      models: mods,
      template: "basic",
      cheatsheet: "none",
      problemCount: pids.length,
      status: "FAILED" as const,
      progress: "0/1 (0%)",
      accuracy: 0,
      totalCost: 0,
      totalTime: "0s",
      results: [failResult],
      summary: [],
    };
  })(),
];

/* ── Main Playground Component ────────────────────���──────────────── */

type RightTab = "run" | "history" | "cheatsheets" | "tasks";

export default function Playground({ onNavigateToShareBoard, onViewShareItem }: { onNavigateToShareBoard?: () => void; onViewShareItem?: (itemId: string) => void } = {}) {
  const [runs, setRuns] = useState<RunHistory[]>(INITIAL_RUNS);
  const [activeRunId, setActiveRunId] = useState<string | null>(INITIAL_RUNS[0].id);
  const [rightTab, setRightTab] = useState<RightTab>("run");
  const [favoriteIds] = useState<Set<string>>(new Set(SOURCE_MY_FAVORITES.map((f) => f.value)));
  const [leftWidth, setLeftWidth] = useState(360);
  const [mobilePanel, setMobilePanel] = useState<"config" | "results">("config");
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(360);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDragging.current = true;
    startX.current = e.clientX;
    startWidth.current = leftWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = ev.clientX - startX.current;
      const newWidth = Math.min(480, Math.max(360, startWidth.current + delta));
      setLeftWidth(newWidth);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [leftWidth]);

  const activeRun = runs.find((r) => r.id === activeRunId) || null;

  const handleRun = useCallback(
    (config: { models: string[]; template: string; problems: string[]; cheatsheet: string }) => {
      const results = generateMockResults(config.models, config.template, config.cheatsheet, config.problems);
      const summary = generateSummary(config.models, config.template, config.cheatsheet, results);
      const newRun: RunHistory = {
        id: Date.now().toString(),
        name: `Run ${runs.length + 1}`,
        timestamp: new Date().toLocaleString("en-US", { year: "numeric", month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false }),
        models: config.models,
        template: config.template,
        cheatsheet: config.cheatsheet,
        problemCount: config.problems.length,
        status: "DONE",
        progress: `${results.length} / ${results.length} (100%)`,
        accuracy: summary.length ? summary.reduce((s, r) => s + r.accuracy, 0) / summary.length : 0,
        totalCost: results.reduce((s, r) => s + r.cost, 0),
        totalTime: results.reduce((s, r) => s + r.time, 0).toFixed(1) + "s",
        results,
        summary,
      };
      setRuns((prev) => [newRun, ...prev]);
      setActiveRunId(newRun.id);
      setRightTab("run");
    },
    [runs.length]
  );

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-90px)] w-full" style={{ borderTop: '1px solid var(--brand-border-subtle)' }}>
      {/* ─── Mobile Tab Switcher ─── */}
      <div className="md:hidden shrink-0 flex" style={{ backgroundColor: 'var(--brand-bg)', borderBottom: '1px solid var(--brand-border-subtle)' }}>
        <button
          onClick={() => setMobilePanel("config")}
          className="flex-1 py-[12px] font-['Helvetica_Neue:Medium',sans-serif] text-[14px] cursor-pointer border-b-2 border-x-0 border-t-0 bg-transparent transition-colors"
          style={{
            color: mobilePanel === "config" ? 'var(--brand-text)' : 'var(--brand-text-opacity-40)',
            borderBottomColor: mobilePanel === "config" ? 'var(--brand-text)' : 'transparent',
          }}
        >
          Configure
        </button>
        <button
          onClick={() => setMobilePanel("results")}
          className="flex-1 py-[12px] font-['Helvetica_Neue:Medium',sans-serif] text-[14px] cursor-pointer border-b-2 border-x-0 border-t-0 bg-transparent transition-colors"
          style={{
            color: mobilePanel === "results" ? 'var(--brand-text)' : 'var(--brand-text-opacity-40)',
            borderBottomColor: mobilePanel === "results" ? 'var(--brand-text)' : 'transparent',
          }}
        >
          Results
        </button>
      </div>

      {/* ─── Left Config Panel ─── */}
      <div className={`shrink-0 ${mobilePanel === "config" ? "flex flex-col flex-1" : "hidden"} md:flex md:flex-col md:flex-none`} style={{ backgroundColor: 'var(--brand-bg)' }}>
        <div className="md:hidden h-full">
          <ConfigPanel onRun={(config) => { handleRun(config); setMobilePanel("results"); }} onNavigateToShareBoard={onNavigateToShareBoard} />
        </div>
        <div className="hidden md:block h-full" style={{ width: leftWidth }}>
          <ConfigPanel onRun={(config) => { handleRun(config); setMobilePanel("results"); }} onNavigateToShareBoard={onNavigateToShareBoard} />
        </div>
      </div>

      {/* ─── Drag Handle (desktop only) ─── */}
      <div
        onMouseDown={handleMouseDown}
        className="hidden md:block shrink-0 w-[5px] cursor-col-resize bg-transparent transition-colors relative group"
        style={{ touchAction: "none" }}
      >
        <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[1px]" style={{ backgroundColor: 'var(--brand-border)' }} />
      </div>

      {/* ─── Right Results Area ─── */}
      <div className={`flex-1 flex-col min-w-0 ${mobilePanel === "results" ? "flex" : "hidden"} md:flex`} style={{ backgroundColor: 'var(--brand-surface-muted)' }}>
        {/* Tab Navigation */}
        <div className="shrink-0 flex items-center px-[28px]" style={{ backgroundColor: 'var(--brand-bg)', borderBottom: '1px solid var(--brand-border-subtle)' }}>
          {([
            { key: "run" as const, label: "Run" },
            { key: "history" as const, label: "History" },
            { key: "cheatsheets" as const, label: "Cheatsheets" },
          ]).map((tab) => (
            <button
              key={tab.key}
              onClick={() => setRightTab(tab.key)}
              className="px-[20px] py-[14px] font-['Helvetica_Neue:Medium',sans-serif] text-[14px] cursor-pointer border-b-2 border-x-0 border-t-0 bg-transparent transition-colors"
              style={{
                color: rightTab === tab.key ? 'var(--brand-text)' : 'var(--brand-text-opacity-40)',
                borderBottomColor: rightTab === tab.key ? 'var(--brand-text)' : 'transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {rightTab === "run" && activeRun ? (
            <RunResultsView run={activeRun} creditsUsed={runs.reduce((s, r) => s + r.results.length * 3, 0)} creditsTotal={50} />
          ) : rightTab === "run" && !activeRun ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="font-['Helvetica_Neue:Regular',sans-serif] text-[14px] text-[rgba(59,14,39,0.3)]">
                Select a model, problems, and a cheatsheet, then click Run to see results.
              </p>
            </div>
          ) : rightTab === "history" ? (
            <HistoryView runs={runs} onSelectRun={(id) => { setActiveRunId(id); setRightTab("run"); }} />
          ) : rightTab === "cheatsheets" ? (
            <CheatsheetsView 
              favoriteIds={favoriteIds} 
              onNavigateToShareBoard={onNavigateToShareBoard}
              onViewShareItem={onViewShareItem}
            />
          ) : (
            <AllTasksView runs={runs} />
          )}
        </div>
      </div>
    </div>
  );
}
